import { useEffect } from "react";

export const useDirections = ({
  isLoaded,
  stops,
  route,
  position,
  map,
  setDirections,
  setFullPath,
  extractPathPoints,
}) => {
  useEffect(() => {
    if (!isLoaded || !stops?.length || !route?.routeCoordinates) return;

    // Generate cache key
    const cacheKey = `directions_${route.id}_${stops
      .map((s) => `${s.stopId}_${s.latitude}_${s.longitude}`)
      .join("-")}`;

    console.log("🧠 [Directions] Cache Key:", cacheKey);

    // Clean up old cache keys for same route
    Object.keys(localStorage).forEach((key) => {
      if (key.startsWith(`directions_${route.id}_`) && key !== cacheKey) {
        console.log(`🗑️ [Directions] Removing old cache entry: ${key}`);
        localStorage.removeItem(key);
      }
    });

    // Check if current key exists
    const cached = localStorage.getItem(cacheKey);
    if (cached) {
      try {
        const { data, timestamp } = JSON.parse(cached);
        if (Date.now() - timestamp < 24 * 60 * 60 * 1000) {
          console.log("✅ [Directions] Using cached directions");
          setDirections(data);
          const allPoints = extractPathPoints(data);
          setFullPath(allPoints);
          if (!position && map) {
            map.setCenter(
              new window.google.maps.LatLng(
                parseFloat(stops[0].latitude),
                parseFloat(stops[0].longitude)
              )
            );
          }
          return;
        } else {
          console.log("⌛ [Directions] Cache expired");
        }
      } catch (e) {
        console.warn("⚠️ [Directions] Cache corrupted, clearing...");
        localStorage.removeItem(cacheKey);
      }
    } else {
      console.log("📭 [Directions] No cache found, making API call");
    }

    // Fetch new directions from API
    const directionsService = new window.google.maps.DirectionsService();
    const waypoints = stops.map((stop) => ({
      location: new window.google.maps.LatLng(
        parseFloat(stop.latitude),
        parseFloat(stop.longitude)
      ),
      stopover: true,
    }));

    const request = {
      origin: waypoints[0].location,
      destination: waypoints[waypoints.length - 1].location,
      waypoints: waypoints.slice(1, -1),
      travelMode: window.google.maps.TravelMode.DRIVING,
      optimizeWaypoints: false,
    };

    console.log("🚀 [Directions] Requesting from API...");
    directionsService.route(request, (result, status) => {
      if (status === window.google.maps.DirectionsStatus.OK) {
        console.log("✅ [Directions] API success, caching new data");
        localStorage.setItem(
          cacheKey,
          JSON.stringify({
            data: result,
            timestamp: Date.now(),
          })
        );
        setDirections(result);
        const allPoints = extractPathPoints(result);
        setFullPath(allPoints);

        if (!position && map) {
          map.setCenter(waypoints[0].location);
        }
      } else {
        console.error("❌ [Directions] API failed:", status);
      }
    });
  }, [stops, route, isLoaded, position, map, extractPathPoints]);
};